
import React from 'react';
import RotatingText from '../RotatingText';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';

const HeroSection: React.FC = () => {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  
  // Use the correct translated words based on the current language
  const rotatingWords = language === 'en' 
    ? ['technology', 'capital', 'growth'] 
    : ['tecnología', 'capital', 'crecimiento'];
  
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Determine the max width based on language and device type
  const getMaxWidth = () => {
    if (language === 'es' && !isMobile) {
      return 'max-w-4xl'; // Wider for Spanish on web
    }
    return 'max-w-3xl'; // Default width for English or mobile
  };
  
  return (
    <section className="relative py-56 bg-[#0D1F2D] dark:bg-noa-rich-black/95 text-white overflow-hidden min-h-screen flex items-center">
      {/* Background patterns */}
      <div className="absolute inset-0 bg-dot-pattern opacity-10"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className={`text-center mx-auto ${getMaxWidth()}`}>
          <div className="uppercase text-xs sm:text-sm font-bold tracking-wider text-noa-antiflash-white/80 mb-6 sm:mb-8">
            {t('noa_partners')}
          </div>
          
          <h1 className="font-space-grotesk text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white dark:text-noa-antiflash-white mb-6 sm:mb-8 leading-tight">
            {t('your_forever')}
            <br />
            <RotatingText 
              words={rotatingWords} 
              className="text-noa-paynes-gray font-light italic" 
            /> {t('partner')}<span className="text-blue-600">.</span>
          </h1>
          
          {/* Subheading text - Updated with language conditional */}
          <p className="text-noa-antiflash-white/80 text-base sm:text-xl max-w-2xl leading-relaxed text-center mx-auto mb-16">
            {language === 'en' 
              ? "We acquire successful B2B software companies in Latin America to hold and grow them forever."
              : "Adquirimos empresas de software B2B exitosas en América Latina para conservarlas y hacerlas crecer para siempre."}
          </p>
          
          {/* Scroll to learn more */}
          <div className="mt-12 sm:mt-16 flex justify-center animate-fade-in opacity-0" style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}>
            <p className="text-noa-antiflash-white/70 text-lg">{t('scroll_to_learn_more')}</p>
          </div>
          <div className="flex justify-center mt-4 animate-bounce">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto">
              <path d="M12 5v14"></path>
              <path d="m19 12-7 7-7-7"></path>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
